import {useCallback, useEffect, useState} from 'react'
import {useNavigate} from 'react-router'
import {useQueryClient} from '@tanstack/react-query'
import Modal from '@/customComponents/uiComponents/modal/Modal.jsx'
import ModalHeader from '@/customComponents/uiComponents/modal/ModalHeader.jsx'
import ModalFooter from '@/customComponents/uiComponents/modal/ModalFooter.jsx'
import {getToken} from '@/utils/authToken'
import {useUser} from '@/hooks/useUser'
import {
    acquireReverb,
    onUserNotification,
    releaseReverb,
    subscribeUserChannel,
    unsubscribeUserChannel,
} from '@/services/reverb'

const SLUG = 'passenger.flight-pickup-updated'

/**
 * "YOUR PICKUP TIME CHANGED" — `passenger.flight-pickup-updated`, as a dialog on any page.
 *
 * ── Why the passenger needs telling at all ────────────────────────────────────────
 * Flight tracking moves a pickup server-side with nothing on the page to show for it: the
 * trip's STATUS does not change, so no trip-channel event fires, and the passenger learned
 * the new time only when something next happened to refetch the ride. The backend
 * broadcasts this on their own user channel precisely so they can be told when it happens.
 *
 * ⚠️ THE SERVER'S OWN WORDS. `title` and `body` are written for this and already carry the
 * new time ("Pickup moved to Jul 24, 19:40"). The ride's authoritative time is still its
 * refetched `scheduled_at` — the queries are invalidated here so every open screen shows it.
 *
 * ⚠️ A QUEUE, DEDUPED ON TRIP. Two notices for one ride keep only the newer (it has the newer
 * time); notices for different rides are shown one after another rather than dropped.
 */
export default function PickupUpdatedModal() {
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const {data: user} = useUser()
    const userId = user?.id ?? null
    const [queue, setQueue] = useState([])

    /* The socket, held open for the whole signed-in session — see `acquireReverb`. */
    useEffect(() => {
        const token = getToken()
        if (!token || !userId) return undefined
        acquireReverb({token})
        subscribeUserChannel(userId)
        return () => {
            unsubscribeUserChannel()
            releaseReverb()
        }
    }, [userId])

    useEffect(() => onUserNotification((payload) => {
        const slug = payload?.slug || payload?.data?.type
        if (slug !== SLUG) return
        const tripId = payload?.trip_id ?? payload?.data?.trip_id ?? null

        if (tripId != null) queryClient.invalidateQueries({queryKey: ['trip', String(tripId)]})
        if (tripId != null) queryClient.invalidateQueries({queryKey: ['trip', tripId]})
        queryClient.invalidateQueries({queryKey: ['trips']})

        const notice = {
            tripId,
            title: payload?.title || 'Your pickup time changed',
            body: payload?.body || payload?.message || null,
        }
        setQueue((current) => [
            ...(tripId == null ? current : current.filter((n) => n.tripId !== tripId)),
            notice,
        ])
    }), [queryClient])

    const current = queue[0] || null
    const dismiss = useCallback(() => setQueue((q) => q.slice(1)), [])
    const viewRide = () => {
        const tripId = current?.tripId
        dismiss()
        if (tripId != null) navigate(`/rides/${tripId}`)
    }

    return (
        <Modal isOpen={!!current} onClose={dismiss}>
            <ModalHeader title={current?.title || 'Your pickup time changed'} onClose={dismiss}/>

            <div className="flex flex-col gap-3 text-center">
                {current?.body && <p className="mb-0! text-(--text-primary)">{current.body}</p>}
                <p className="mb-0! text-(--text-secondary)">
                    Your flight changed, so we moved your pickup to match. Your driver already knows —
                    there&apos;s nothing you need to do.
                </p>
            </div>

            <ModalFooter>
                <div className="flex flex-col gap-3">
                    {current?.tripId != null && (
                        <button
                            type="button"
                            onClick={viewRide}
                            className="bg-(--text-primary) text-white text-center rounded-full w-full p-3 hover:opacity-80 cursor-pointer"
                        >
                            View ride
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={dismiss}
                        className="text-center rounded-full w-full p-3 border border-[#CFCDCD] hover:bg-[#F3F4F6] cursor-pointer"
                    >
                        Got it
                    </button>
                </div>
            </ModalFooter>
        </Modal>
    )
}
